import { FormEvent, useCallback, useEffect, useState } from 'react';
import { generatePassword, UserRow, users } from '../api';
import { useAuth } from '../auth';
import { ago, dayLabel } from '../format';
import { PasswordField } from '../pages/AdminUsers';

// A customer's logins for the customer app (app.fleetfunctions.ie). Any staff member can open
// the app as one of them, without their password; admins add, disable and reset them.
export function AppLogins({ customerId }: { customerId: number }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [shown, setShown] = useState<{ email: string; password: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => users.forCustomer(customerId).then((r) => setRows(r.users)).catch((e) => setError(e.message)), [customerId]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      await users.addForCustomer(customerId, form);
      setShown({ email: form.email.trim().toLowerCase(), password: form.password });
      setForm({ name: '', email: '', password: '' });
      setAdding(false);
    });
  };

  const reset = (u: UserRow) => void act(async () => {
    const password = generatePassword();
    await users.resetPassword(u.id, password);
    setShown({ email: u.email, password });
  });

  // Open the tab straight away (pop-up blockers allow that only on the click itself), then
  // send it to the one-time link.
  const viewAs = (u: UserRow) => {
    const tab = window.open('about:blank', '_blank');
    void act(async () => {
      try {
        const { url } = await users.viewAs(u.id);
        if (tab) tab.location.href = url;
        else window.location.href = url;
      } catch (err) {
        tab?.close();
        throw err;
      }
    });
  };

  return (
    <section className="card form" aria-label="App logins">
      <div className="page-head">
        <h2>App logins</h2>
        {isAdmin && !adding && (
          <button className="link" onClick={() => { setAdding(true); setShown(null); setForm({ name: '', email: '', password: generatePassword() }); }}>Add login</button>
        )}
      </div>
      <p className="muted small">Logins for the customer app at app.fleetfunctions.ie. Each one sees only this customer's vehicles.</p>
      {error && <p className="error" role="alert">{error}</p>}
      {shown && (
        <div className="callout" role="status">
          <strong>Pass this on to the customer.</strong> It's shown only once, and they choose their own password when they first log in.
          <p className="mono">{shown.email}<br />{shown.password}</p>
        </div>
      )}
      {adding && (
        <form className="form" onSubmit={add}>
          <div className="grid2">
            <label>Name<input id="al-name" type="text" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Email<input id="al-email" type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          </div>
          <PasswordField id="al-pw" value={form.password} onChange={(p) => setForm({ ...form, password: p })} />
          <div className="actions">
            <button className="primary" disabled={busy}>Add login</button>
            <button type="button" className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}
      {rows && rows.length === 0 && !adding && <p className="muted">No app logins yet.</p>}
      {rows && rows.length > 0 && (
        <ul className="logins">
          {rows.map((u) => (
            <li key={u.id}>
              <span>
                <strong>{u.name}</strong> <span className="muted small">{u.email}</span><br />
                <span className="muted small">
                  {u.deletedAt ? `Account deleted ${dayLabel(u.deletedAt)}; details removed`
                    : u.deletionRequestedAt ? `Deleted their account ${dayLabel(u.deletionRequestedAt)} · details removed on ${dayLabel(u.wipeAfter!)}`
                    : u.disabled ? 'Disabled' : u.lastLogin ? `Last login ${ago(u.lastLogin)}` : 'Never logged in'}
                  {u.mustChangePassword && !u.disabled && ' · must choose a password'}
                </span>
              </span>
              <span className="unit-links">
                {!u.disabled && <button className="button" disabled={busy} onClick={() => viewAs(u)}>Open app as</button>}
                {isAdmin && !u.disabled && <button className="link" disabled={busy} onClick={() => reset(u)}>Reset password</button>}
                {isAdmin && !u.deletionRequestedAt && <button className={`link${u.disabled ? '' : ' danger'}`} disabled={busy} onClick={() => void act(() => users.setDisabled(u, !u.disabled))}>{u.disabled ? 'Enable' : 'Disable'}</button>}
                {isAdmin && u.deletionRequestedAt && !u.deletedAt && (
                  <button className="link" disabled={busy} onClick={() => {
                    if (window.confirm(`Restore ${u.name}'s login? They deleted it themselves in the app; only do this if they asked.`)) void act(() => users.cancelDeletion(u.id));
                  }}>Cancel deletion</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
